import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, MapPin, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const empty = { name: "", email: "", subject: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("All fields are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm(empty);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative bg-navy text-white overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-20">
          <span className="overline-label text-electric">Contact</span>
          <h1 className="mt-3 font-display text-4xl lg:text-6xl font-extrabold">Let's talk</h1>
          <p className="mt-5 text-white/75 max-w-2xl text-lg">
            Questions about insights, research, solutions or partnerships? Send us a note.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
        <div className="space-y-6">
          {[
            { icon: Mail, title: "Email", value: "info@lead-ites.com" },
            { icon: MessageSquare, title: "Partnerships", value: "partners@lead-ites.com" },
            { icon: MapPin, title: "Office", value: "Roscoe Blvd, Panorama City, Los Angeles, CA 91402, US" },
          ].map((c) => (
            <div key={c.title} className="flex gap-4" data-testid={`contact-info-${c.title.toLowerCase()}`}>
              <div className="h-11 w-11 rounded-lg bg-navy flex items-center justify-center shrink-0">
                <c.icon className="h-5 w-5 text-electric" />
              </div>
              <div>
                <p className="font-display font-bold text-navy">{c.title}</p>
                <p className="text-sm text-muted-foreground">{c.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-2 rounded-2xl border border-border bg-white p-8 shadow-sm">
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Full name *</Label><Input value={form.name} onChange={set("name")} data-testid="contact-name" /></div>
              <div className="space-y-1.5"><Label>Email *</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="contact-email" /></div>
            </div>
            <div className="space-y-1.5"><Label>Subject *</Label><Input value={form.subject} onChange={set("subject")} data-testid="contact-subject" /></div>
            <div className="space-y-1.5"><Label>Message *</Label><Textarea rows={5} value={form.message} onChange={set("message")} data-testid="contact-message" /></div>
            <Button type="submit" disabled={loading} className="btn-electric rounded-full px-8" data-testid="contact-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Message"}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
